import { useRef } from 'react';
import { toast } from 'sonner';
import { ClipboardPaste, LayoutList } from 'lucide-react';
import type { SlideImageLayout, SlideImageSize, TrainingSlideDef } from '@hcc/types';
import { SLIDE_IMAGE_LAYOUTS, SLIDE_IMAGE_SIZES } from '@hcc/types';
import { ApiError } from '../../../lib/api';
import { uploadFormImage } from '../../../lib/upload';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';
import { ImageUploadInput } from './ImageUploadInput';
import { MarkupToolbar } from './MarkupToolbar';
import { BlockEditorList } from './blocks/BlockEditorList';
import { convertSlideToBlocks, makeBlock } from './blocks/blockOps';
import { useImagePaste } from './useImagePaste';

const LAYOUT_LABEL: Record<SlideImageLayout, string> = {
  below: 'Below the text',
  left: 'Left of the text',
  right: 'Right of the text',
  full: 'Full width, above the text',
};

const SIZE_LABEL: Record<SlideImageSize, string> = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
};

/**
 * Editor for one content slide: title, body markup (with the formatting
 * toolbar), and the image with its placement controls. The live preview on
 * the right of the builder shows exactly what the inductee will see.
 */
export function ContentPageEditor({
  slide,
  onChange,
}: {
  slide: TrainingSlideDef;
  onChange: (next: TrainingSlideDef) => void;
}) {
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Paste-to-insert: Ctrl+V a screenshot/snip straight into a block-based slide
  // and it lands as an image block (uploaded like any other image). Only the
  // block editor supports it; legacy body/image slides keep the single slot.
  const pasting = useImagePaste(slide.blocks !== undefined, async (file) => {
    try {
      const named = file.name
        ? file
        : new File([file], `pasted-${Date.now()}.png`, { type: file.type || 'image/png' });
      const url = await uploadFormImage(named);
      const base = makeBlock('image');
      const imageBlock = base.type === 'image' ? { ...base, image_key: url } : base;
      onChange({ ...slide, blocks: [...(slide.blocks ?? []), imageBlock] });
      toast.success('Image pasted');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not paste the image');
    }
  });

  const titleField = (
    <label className="block text-sm">
      <span className="font-medium text-fg-muted">Slide title</span>
      <input
        value={slide.title ?? ''}
        onChange={(e) => onChange({ ...slide, title: e.target.value })}
        placeholder="e.g. Why do they occur?"
        className={SHARED_INPUT_CLASS}
      />
    </label>
  );

  // Block-based slides (everything authored from now on, plus converted
  // legacy ones) get the structured editor.
  if (slide.blocks !== undefined) {
    return (
      <div className="space-y-3">
        {titleField}
        <BlockEditorList
          blocks={slide.blocks}
          onChange={(blocks) => onChange({ ...slide, blocks })}
          allowColumns
        />
        <p className="flex items-center gap-1.5 text-xs text-fg-dim" aria-live="polite">
          <ClipboardPaste className="h-3.5 w-3.5 shrink-0" />
          {pasting ? 'Pasting image…' : 'Paste a screenshot (Ctrl+V) to add it as an image.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {titleField}
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<LayoutList className="h-4 w-4" />}
          onClick={() => onChange(convertSlideToBlocks(slide))}
        >
          Switch to block editing
        </Button>
      </div>
      <div className="text-sm">
        <div className="flex items-end justify-between gap-2">
          <span className="font-medium text-fg-muted">Body</span>
          <MarkupToolbar
            textareaRef={bodyRef}
            value={slide.body ?? ''}
            onChange={(body) => onChange({ ...slide, body })}
          />
        </div>
        <textarea
          ref={bodyRef}
          rows={8}
          value={slide.body ?? ''}
          onChange={(e) => onChange({ ...slide, body: e.target.value })}
          placeholder={'Plain text. Blank line = new paragraph.'}
          className={SHARED_INPUT_CLASS}
        />
        <p className="mt-1 text-xs text-fg-dim">
          {'**bold** · ## subheading · - bullet · 1. numbered · > callout box'}
        </p>
      </div>
      <ImageUploadInput
        value={slide.image_key}
        onChange={(url) =>
          onChange(
            url
              ? { ...slide, image_key: url }
              : {
                  ...slide,
                  image_key: undefined,
                  image_layout: undefined,
                  image_size: undefined,
                  image_caption: undefined,
                  image_alt: undefined,
                },
          )
        }
      />
      {slide.image_key ? (
        <div className="space-y-3 rounded-token-sm border border-line/10 bg-surface-2 p-3">
          <div className="flex flex-wrap gap-3">
            <label className="text-sm">
              <span className="font-medium text-fg-muted">Position</span>
              <select
                value={slide.image_layout ?? 'below'}
                onChange={(e) =>
                  onChange({ ...slide, image_layout: e.target.value as SlideImageLayout })
                }
                className={`${SHARED_INPUT_CLASS} w-auto`}
              >
                {SLIDE_IMAGE_LAYOUTS.map((l) => (
                  <option key={l} value={l}>
                    {LAYOUT_LABEL[l]}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="font-medium text-fg-muted">Size</span>
              <select
                value={slide.image_size ?? 'medium'}
                disabled={slide.image_layout === 'full'}
                onChange={(e) =>
                  onChange({ ...slide, image_size: e.target.value as SlideImageSize })
                }
                className={`${SHARED_INPUT_CLASS} w-auto disabled:opacity-50`}
              >
                {SLIDE_IMAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {SIZE_LABEL[s]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Caption (optional)</span>
            <input
              value={slide.image_caption ?? ''}
              onChange={(e) => onChange({ ...slide, image_caption: e.target.value || undefined })}
              placeholder="Shown under the picture"
              className={SHARED_INPUT_CLASS}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">
              Describe the image (for screen readers)
            </span>
            <input
              value={slide.image_alt ?? ''}
              onChange={(e) => onChange({ ...slide, image_alt: e.target.value || undefined })}
              placeholder="e.g. A trailing cable across an office walkway"
              className={SHARED_INPUT_CLASS}
            />
          </label>
        </div>
      ) : null}
    </div>
  );
}
